/** Shared optics types. Units: dioptres (D), millimetres (mm), degrees, arcmin. See docs/OPTICS.md. */

export type Channel = 'r' | 'g' | 'b';

/** Reference wavelengths per colour channel, nm (OPTICS §1). */
export const WAVELENGTH_NM: Record<Channel, number> = { r: 610, g: 550, b: 460 };

/** Longitudinal chromatic aberration relative to 550 nm, D (OPTICS §2). */
export const LCA: Record<Channel, number> = { r: 0.29, g: 0, b: -0.69 };

export const CHANNELS: Channel[] = ['r', 'g', 'b'];

/** One focal component of the optical system (OPTICS §5.1). */
export interface FocalComponent {
  /** Energy weight for diffractive orders; 1 for zonal / single components. */
  w: number;
  /** Fixed add power, D. 0 for the distance focus. */
  add: number;
  /** Diffractive orders scale their add with wavelength. */
  diffractive: boolean;
  /** Radial zone [r0, r1) in mm on the pupil. Undefined = whole pupil. */
  r0?: number;
  r1?: number;
  /** Zonal add profile P(r) in D (refractive EDOF / mono+), evaluated per ray. */
  profile?: (r: number) => number;
}

/** Zernike-like higher-order aberration coefficients, µm, defined for a 6 mm pupil (OPTICS §4). */
export interface Hoa {
  c40: number;
  c31: number;
  c31h: number;
  c33: number;
  c33s: number;
}

/** Everything the renderer and the VA estimator need for one pane. */
export interface PaneConfig {
  age: number;
  /** Pupil diameter for the current scene lighting, mm. */
  pupil: number;
  /** Accommodation amplitude A, D. */
  amplitude: number;
  /** Residual sphere after correction, D (myopia negative). */
  resS: number;
  /** Residual cylinder after correction, D (minus-cyl, ≤ 0). */
  resC: number;
  /** Cylinder axis, degrees. */
  axis: number;
  /** Reading add of progressive glasses, D. 0 when not progressive. */
  progressiveAdd: number;
  components: FocalComponent[];
  hoa: Hoa;
  /** Total scatter fraction f (OPTICS §8). */
  scatter: number;
  /** Linear RGB transmission multipliers. */
  transmission: [number, number, number];
  /** Cataract MAR floor, arcmin. */
  marCat: number;
  /** Retinal MAR floor, arcmin (M4). */
  marRet: number;
  /** ICL ring halo part (M3). */
  iclRing: boolean;
  /** Retinal image magnification of the correction (aphakic spectacles ≈ 1.28, D-021); 1 = none. */
  magnification: number;
  /** Energy fraction of starburst streaks around bright lights (D-018). */
  streaks: number;
  /** Streak pattern: 1 = natural lens (suture rays), 2 = no natural lens. */
  streakPattern: number;
  /** Stable cache key derived from all of the above. */
  key: string;
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Spectacle add of progressive glasses for a layer at vergence V (OPTICS §6). */
export function progressiveAddAt(cfg: PaneConfig, V: number): number {
  if (cfg.progressiveAdd <= 0 || V < 0.5) return 0;
  return V < 2 ? 0.6 * cfg.progressiveAdd : cfg.progressiveAdd;
}

/** Accommodation the eye applies to look at a layer at vergence V (OPTICS §3). */
export function accommodationAt(cfg: PaneConfig, V: number): number {
  return clamp(V + cfg.resS + cfg.resC / 2 - progressiveAddAt(cfg, V), 0, cfg.amplitude);
}

/** Sphere defocus at 550 nm for a focal component with fixed add (OPTICS §3). */
export function sphereDefocus(cfg: PaneConfig, V: number, add: number): number {
  return V + cfg.resS - progressiveAddAt(cfg, V) - accommodationAt(cfg, V) - add;
}
